/**
 * Clients, and the files they send.
 */
import { z } from 'zod';
import { declaredTypeSchema, fileStatusSchema } from './enums';
import { moduleOrDefaultSchema } from './modules';

export const clientRecordSchema = z.object({
	pkClientId: z.string(),
	name: z.string(),
	code: z.string(),
	erp: z.string().nullable(),
	gstin: z.string().nullable(),
	files: z.number().optional(),
	audits: z.number().optional(),
});

export const detectedColumnSchema = z.object({
	name: z.string(),
	samples: z.array(z.string()),
	dtype: z.string(),
});

export const fileRecordSchema = z.object({
	pkFileId: z.string(),
	fkClientId: z.string(),
	originalName: z.string(),
	/* Null for a reader without data-preparation access: the server withholds
     storage locations, hashes, detected columns and raw errors from them. */
	s3RawKey: z.string().nullable(),
	s3NormalisedParquetKey: z.string().nullable(),
	/* Present only on the response to `upload-complete`, which starts an ingest
     and hands back the run it started. Optional because every other place a
     file record is returned has no run to name. */
	ingestRunId: z.string().optional(),
	status: fileStatusSchema,
	contentType: z.string().nullable(),
	sizeBytes: z.number().nullable(),
	rowCount: z.number().nullable(),
	sheetName: z.string().nullable(),
	headerRow: z.number().nullable(),
	sourceColumns: z.array(detectedColumnSchema).nullable(),

	declaredAs: declaredTypeSchema,
	sourceSystem: z.string().nullable(),
	periodLabel: z.string().nullable(),
	periodStart: z.string().nullable(),
	periodEnd: z.string().nullable(),
	fkLayoutId: z.string().nullable(),
	layoutMatch: z.string().nullable(),
	contentHash: z.string().nullable(),
	columnsTotal: z.number().nullable(),
	columnsMapped: z.number().nullable(),
	statusNote: z.string().nullable(),
	classifiedSales: z.number().nullable(),
	classifiedReturns: z.number().nullable(),
	classifiedAdjust: z.number().nullable(),
	classifiedUnknown: z.number().nullable(),
	excludedReason: z.string().nullable(),
	fkReplacedById: z.string().nullable(),
	/* The file groups this file is in. Optional: older responses omit it. */
	fileGroups: z
		.array(z.object({ id: z.string(), name: z.string() }))
		.optional(),
	module: moduleOrDefaultSchema,

	errorMessage: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	/* Who uploaded it, and the last edit by a person. Only the files list
     (`GET /files`) carries these. */
	/* Set when the file was deleted (soft delete); seen only through an audit. */
	deletedAt: z.string().nullable().optional(),
	createdByName: z.string().nullable().optional(),
	lastEditedAt: z.string().nullable().optional(),
	lastEditedByName: z.string().nullable().optional(),
});

export const uploadTicketSchema = z.object({
	fileId: z.string(),
	uploadUrl: z.string(),
	s3Key: z.string(),
	expiresInSeconds: z.number(),
});

export type ClientRecord = z.infer<typeof clientRecordSchema>;
export type DetectedColumn = z.infer<typeof detectedColumnSchema>;
export type FileRecord = z.infer<typeof fileRecordSchema>;
export type UploadTicket = z.infer<typeof uploadTicketSchema>;
